import { LanguageEnum } from "enums/LanguageEnum";
import { createLogger } from "logging";
import React, { PropsWithChildren, useCallback, useMemo } from "react";
import { useEffect } from "react";
import { useCookies } from "react-cookie";
import { germanLabels } from "translations/de";
import { englishLabels } from "translations/en";
import { frenchLabels } from "translations/fr";
import { italianLabels } from "translations/it";

import { TranslationsContext, TranslationsContextProps } from "./TranslationsContext";

const logger = createLogger("TranslationsProvider");

const labels = {
  [LanguageEnum.DE]: germanLabels,
  [LanguageEnum.FR]: frenchLabels,
  [LanguageEnum.IT]: italianLabels,
  [LanguageEnum.EN]: englishLabels,
};

const cookieKey = "i18n";

// Get browser language, if language is something like "de-CH", only take "de"
const allLanguages = Object.values(LanguageEnum);
const browserLanguage = navigator.language.split("-")[0] as Extract<LanguageEnum, string>;
logger.debug(`browserLanguage:='${browserLanguage}'`);

const defaultLanguage = allLanguages.find((l) => l === browserLanguage) || LanguageEnum.DE;
logger.debug(`defaultLanguage:='${defaultLanguage}'`);

export const TranslationsProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const [cookies, setCookie] = useCookies([cookieKey], { doNotParse: true, doNotUpdate: true });
  const cookieValue = cookies[cookieKey];

  const initialLanguage = allLanguages.find((l) => l === cookieValue) || defaultLanguage;
  logger.debug(`render(): cookie:='${cookieValue}', resolved:='${initialLanguage}'`);

  const [currentLanguage, setLanguage] = React.useState(initialLanguage);

  const changeLanguage = useCallback(
    (newLanguage: LanguageEnum) => {
      logger.debug(`changeLanguage(): '${currentLanguage}' -> '${newLanguage}'`);
      setLanguage(newLanguage);

      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);
      setCookie(cookieKey, newLanguage, { path: "/", expires: expirationDate });
    },
    [currentLanguage, setCookie]
  );

  useEffect(() => {
    const before = document.documentElement.lang;
    const after = `${currentLanguage}`.toLowerCase();
    if (before != after) {
      logger.debug(`useEffect(): html.lang changed to '${after}' (was: '${before}')`);
      document.documentElement.lang = after;
    }
  }, [currentLanguage]);

  useEffect(() => {
    const before = window.labels;
    const after = labels[currentLanguage];
    if (before != after) {
      logger.debug(`useEffect(): global labels changed to '${currentLanguage}'`);
      window.labels = after;
    }
    window.currentLanguage = currentLanguage;
  }, [currentLanguage]);

  const state = useMemo<TranslationsContextProps>(
    () => ({
      ...labels[currentLanguage],
      currentLanguage,
      allLanguages,
      changeLanguage,
    }),
    [currentLanguage, changeLanguage]
  );

  return <TranslationsContext.Provider value={state}>{children}</TranslationsContext.Provider>;
};
